import type { ListenerEntry, ListenerFactory, RemoteAnalyticsConfig } from '../types/config.js';
import type { AnalyticsListener } from '../types/listener.js';
import { createNoopListener } from './noop.js';
import { createClarityListener } from './clarity.js';
import { createGAListener } from './ga.js';
import { createCustomApiListener } from './custom-api.js';

/** Built-in listener IDs supported by the SDK (noop, clarity, ga, custom-api). */
export const BUILT_IN_LISTENER_IDS = ['noop', 'clarity', 'ga', 'custom-api'] as const;

const BUILT_IN_FACTORIES: Record<string, ListenerFactory> = {
  noop: () => createNoopListener(),
  clarity: (config) => createClarityListener(config as { projectId: string }),
  ga: (config) => createGAListener(config as { measurementId: string }),
  'custom-api': (config) => createCustomApiListener(config as Parameters<typeof createCustomApiListener>[0]),
};

/**
 * Returns the built-in listener factory for the given id, or undefined if unknown.
 */
export function getBuiltInListenerFactory(id: string): ListenerFactory | undefined {
  return BUILT_IN_FACTORIES[id];
}

export interface BuildListenersFromRemoteConfigOptions {
  /** App override: map listener id to factory. When omitted, built-in registry is used. */
  factories?: Record<string, ListenerFactory>;
  /** Called when a remote listener id has no factory (unknown id). */
  onError?: (error: Error, context?: { listenerId?: string }) => void;
}

/**
 * Builds ListenerEntry[] from remote config. Uses options.factories when provided,
 * otherwise the built-in registry. Skips entries with enabled === false or unknown id.
 */
export function buildListenersFromRemoteConfig(
  remote: RemoteAnalyticsConfig,
  options?: BuildListenersFromRemoteConfigOptions
): ListenerEntry[] {
  const factories = options?.factories ?? BUILT_IN_FACTORIES;
  const onError = options?.onError;
  const entries: ListenerEntry[] = [];

  if (!Array.isArray(remote.listeners)) {
    return entries;
  }

  for (const entry of remote.listeners) {
    if (entry.enabled === false) continue;
    const id = entry.id;
    if (id == null || typeof id !== 'string') continue;

    const factory = factories[id];
    if (factory == null) {
      onError?.(new Error(`Unknown listener id: ${id}`), { listenerId: id });
      continue;
    }

    let listener: AnalyticsListener;
    try {
      listener = factory(entry.config);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      onError?.(error, { listenerId: id });
      continue;
    }

    entries.push({
      id,
      enabled: entry.enabled,
      listener,
      config: entry.config,
    });
  }

  return entries;
}
